import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, Plus } from 'lucide-react';
import { interpolateText, FormParameterValues } from '@client/hooks/useFormParameters';
import {
  validateField,
  formatters,
  unformatters,
} from '@client/util/policyApplicationFieldValidation';
import { useNavigation } from '@client/contexts/NavigationContext';
import {
  validateRequiredQuestion,
  isTableCellMissingRequiredValue,
  isRequiredFieldError,
} from '@client/util/questionValidation';
import { useQuestionState } from '@client/hooks/useFormConditionals';
import { FormFieldParser } from '@shared/application-form/FormFieldParser';
import ReactMarkdown from 'react-markdown';
import { cn, preserveBlankLines } from '@client/lib';
import QuestionCitationTooltip from '@client/components/policy-application/QuestionCitationTooltip';
import { calculateCompositeValue } from '@shared/policy-application/compositeFieldUtils';
import { evaluateCondition } from '@shared/policy-application/conditionalLogicUtils';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { SessionType } from '@shared/SessionType';

const handleSelectChange = (newValue: string, onChangeCallback: (value: string) => void) => {
  if (newValue === '__CLEAR__') {
    onChangeCallback('');
  } else {
    onChangeCallback(newValue);
  }
};
const renderClearOption = (hasValue: boolean) => {
  if (!hasValue) return null;
  return (
    <SelectItem
      value="__CLEAR__"
      className="py-3 italic text-gray-500 sm:py-1.5"
    >
      Clear selection
    </SelectItem>
  );
};

const shouldApplyComposite = (
  question: PolicyFormQuestion,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues,
): boolean => {
  if (!question.composite) return false;

  // If conditional is provided, evaluate it
  if (question.composite.conditional) {
    return evaluateCondition(question.composite.conditional, formData, parameters);
  }

  // No conditional means always apply composite
  return true;
};

const filterVisibleOptions = (
  options:
    | { id: string; value: string; wizardOnly?: boolean; conditional?: OptionConditionalBehavior }[]
    | undefined,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues,
) => {
  if (!options) return [];

  return options.filter(option => {
    // If no conditional is defined, option is visible
    if (!option.conditional?.visible) return true;

    // Evaluate the visibility condition
    return evaluateCondition(option.conditional.visible, formData, parameters);
  });
};

interface QuestionFieldProps {
  question: PolicyFormQuestion;
  value: PolicyFormSubmissionDataValue;
  onChange: (value: PolicyFormSubmissionDataValue) => void;
  parameters?: FormParameterValues;
}

const QuestionField: React.FC<QuestionFieldProps> = ({
  question,
  value,
  onChange,
  parameters = {},
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const { validationState, actions, state, formDefinition, prefillCitations } = useNavigation();
  const session = useSelector(selectSession);
  const isWizard = session.sessionType === SessionType.wizard;

  // Get citations for this specific question
  const questionCitations = prefillCitations?.[question.questionRef];

  // Use the established hook pattern for conditional logic
  const questionState = useQuestionState(question, formDefinition, state.formData, parameters);

  // Get validation error from centralized state
  const validationError = validationState.fieldValidationErrors.get(question.questionRef);

  // Determine which title to use: conditionalTitle (if text condition is true) → titleOverride → title
  const effectiveTitle =
    questionState.showConditionalText && question.conditionalTitle
      ? question.conditionalTitle
      : question.titleOverride || question.title;

  // Check if there are any wizard-only options
  const hasWizardOnlyOptions = question.options?.some(option => option.wizardOnly) ?? false;

  // Determine which helperText to use: conditionalHelperText (if text condition is true) → helperText
  const effectiveHelperText =
    questionState.showConditionalText && question.conditionalHelperText
      ? question.conditionalHelperText
      : question.helperText;

  const interpolatedTitle = preserveBlankLines(interpolateText(effectiveTitle, parameters));
  const interpolatedHelperText = effectiveHelperText
    ? preserveBlankLines(effectiveHelperText)
    : undefined;
  const interpolatedPlaceholder = question.placeholder
    ? interpolateText(question.placeholder, parameters)
    : question.placeholder;

  const visibleOptions = filterVisibleOptions(question.options, state.formData, parameters);
  const interpolatedOptions = visibleOptions.map(option => ({
    ...option,
    value: interpolateText(option.value, parameters),
  }));

  useEffect(() => {
    if (
      question.validationType &&
      formatters[question.validationType] &&
      value !== undefined &&
      value !== null
    ) {
      setDisplayValue(formatters[question.validationType](String(value)));
    } else {
      setDisplayValue(String(value || ''));
    }
  }, [value, question.validationType]);

  // Check for required field validation only when showValidationErrors is true.
  // This prevents "This field is required" errors from showing on initial form load.
  // The flag is set to true when user clicks Next, triggering required field checks.
  useEffect(() => {
    // If question is not conditionally required, always clear any validation errors
    if (!questionState.required) {
      if (isRequiredFieldError(validationError)) {
        actions.setFieldValidationError(question.questionRef, undefined);
      }
      return;
    }

    // Only validate if showValidationErrors is true and question is required
    if (validationState.showValidationErrors && questionState.required) {
      // For composite fields, check if there's a composite value even if the raw value is empty
      let effectiveValue = value;

      if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
        // For composite fields, calculate the composite value to check if field is actually filled
        const compositeValue = calculateCompositeValue(
          question,
          state.formData,
          formDefinition.questions,
          parameters,
        );

        // If there's a composite value, the field is not empty
        if (compositeValue !== undefined && compositeValue !== null && compositeValue !== '') {
          effectiveValue = compositeValue;
        }

        // For compositeFieldRef with allowAppendText, check the separate composite field
        if (question.composite.compositeFieldRef) {
          const compositeFieldValue = state.formData[question.composite.compositeFieldRef];
          if (
            compositeFieldValue !== undefined &&
            compositeFieldValue !== null &&
            compositeFieldValue !== ''
          ) {
            effectiveValue = compositeFieldValue;
          }
        }
      }

      const validationResult = validateRequiredQuestion(question, effectiveValue);

      if (!validationResult.isValid) {
        actions.setFieldValidationError(question.questionRef, validationResult.errorMessage!);
      } else {
        // Clear the required error if field now has a value
        if (isRequiredFieldError(validationError)) {
          actions.setFieldValidationError(question.questionRef, undefined);
        }
      }
    }
  }, [
    validationState.showValidationErrors,
    questionState.required,
    value,
    validationError,
    question.questionRef,
    actions,
    question.composite,
    state.formData,
    parameters,
    formDefinition.questions,
  ]);

  useEffect(() => {
    if (
      question.composite &&
      !question.composite.compositeFieldRef &&
      shouldApplyComposite(question, state.formData, parameters)
    ) {
      const compositeValue = calculateCompositeValue(
        question,
        state.formData,
        formDefinition.questions,
        parameters,
      );

      const parsedValue = FormFieldParser.parse(question, compositeValue);
      onChange(parsedValue);
    }
  }, []);

  const handleChange = (newValue: string | number | boolean, fieldType?: string) => {
    if (!question.validationType) {
      if (fieldType === 'number') {
        onChange(Number(newValue) || '');
      } else {
        onChange(newValue);
      }
      return;
    }

    let rawValue = String(newValue);
    const validationType = question.validationType;

    if (unformatters[validationType]) {
      rawValue = unformatters[validationType](String(newValue));
    }

    // Real-time format validation (email, phone, etc.) - always active
    if (rawValue && rawValue.trim() !== '') {
      const validation = validateField(rawValue, validationType);
      actions.setFieldValidationError(question.questionRef, validation.error);
    } else if (question.required && rawValue.trim() === '') {
      // Required field validation during typing (provides immediate feedback when clearing a required field)
      actions.setFieldValidationError(question.questionRef, 'This field is required');
    } else {
      // Clear any existing validation error
      actions.setFieldValidationError(question.questionRef, undefined);
    }

    setDisplayValue(String(newValue));
    onChange(rawValue);
  };

  const handleBlur = () => {
    if (question.validationType && formatters[question.validationType] && value) {
      const formatted = formatters[question.validationType](String(value));
      setDisplayValue(formatted);
    }
  };

  const renderValidationError = () => {
    if (validationError) {
      return <p className="mt-1 text-sm text-red-500">{validationError}</p>;
    }
    return null;
  };

  const getErrorStyling = (baseClasses: string) => {
    return `${baseClasses} ${validationError ? 'border-red-500' : ''}`;
  };

  const renderField = () => {
    switch (question.type) {
      case 'text': {
        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const allowAppend = question.composite.allowAppendText ?? false;
          const compositeFieldRef = question.composite.compositeFieldRef;
          const compositeValue = calculateCompositeValue(
            question,
            state.formData,
            formDefinition.questions,
            parameters,
          );

          const parsedValue = FormFieldParser.parse(question, compositeValue);

          if (allowAppend && compositeFieldRef) {
            // Render two separate input fields with separate storage:
            // 1. Read-only composite value (stored in compositeFieldRef, always auto-calculated)
            // 2. Editable user text field (stored in this question's field, user's custom text)
            const userCustomText = (value as string) || '';

            return (
              <div className="space-y-2">
                {/* Read-only composite value */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Prefilled Information</Label>
                  {question.composite.compositeTitle && (
                    <p className="mb-2 text-sm text-gray-600">
                      {question.composite.compositeTitle}
                    </p>
                  )}
                  <Input
                    type="text"
                    value={parsedValue}
                    className="border-primary-200 bg-gray-50"
                    disabled
                    readOnly
                    data-private
                  />
                </div>
                {/* Editable user text field */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Additional Information
                  </Label>
                  <Input
                    type="text"
                    placeholder={interpolatedPlaceholder || 'Enter additional information...'}
                    value={userCustomText}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={question.validationType ? handleBlur : undefined}
                    className={getErrorStyling('border-primary-200')}
                    disabled={question.disabled}
                    data-private
                  />
                  {renderValidationError()}
                </div>
              </div>
            );
          }

          if (allowAppend) {
            // Single editable field with composite value
            // If DB value exists and differs from composite value, use DB value (user has edited)
            // Otherwise, use composite value (initialize with auto-generated text)
            const dbValue = (value as string) || '';
            const currentValue = dbValue && dbValue !== parsedValue ? dbValue : parsedValue;

            return (
              <div>
                <Input
                  type="text"
                  placeholder={interpolatedPlaceholder || 'Enter additional information...'}
                  value={currentValue}
                  onChange={e => handleChange(e.target.value)}
                  onBlur={question.validationType ? handleBlur : undefined}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          } else {
            // For non-allowAppend composite fields, keep them read-only
            return (
              <div>
                <Input
                  type="text"
                  placeholder={interpolatedPlaceholder}
                  value={parsedValue}
                  onChange={({ target }) => handleChange(target.value)}
                  onBlur={question.validationType ? handleBlur : undefined}
                  className={cn(getErrorStyling('border-primary-200'), {
                    invisible: question.hidden,
                  })}
                  disabled={question.disabled}
                  readOnly
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          }
        }

        return (
          <div>
            <Input
              type="text"
              placeholder={interpolatedPlaceholder}
              value={question.validationType ? displayValue : (value as string) || ''}
              onChange={e => handleChange(e.target.value)}
              onBlur={question.validationType ? handleBlur : undefined}
              className={getErrorStyling('border-primary-200')}
              disabled={question.disabled}
              maxLength={question.characterLimit?.mode === 'strict' ? question.characterLimit.limit : undefined}
              data-private
            />
            {question.characterLimit && (
              <div className="mt-1 text-xs text-gray-500">
                {((value as string) || '').length} / {question.characterLimit.limit} characters
                {question.characterLimit.mode === 'overflow' && ((value as string) || '').length > question.characterLimit.limit && (
                  <span className="ml-2 text-orange-600">
                    (will be moved to overflow form)
                  </span>
                )}
              </div>
            )}
            {renderValidationError()}
          </div>
        );
      }

      case 'number': {
        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const allowAppend = question.composite.allowAppendText ?? false;
          const compositeValue = calculateCompositeValue(
            question,
            state.formData,
            formDefinition.questions,
            parameters,
          );

          if (allowAppend) {
            // Single editable field with composite value
            // If DB value exists and differs from composite value, use DB value (user has edited)
            // Otherwise, use composite value (initialize with auto-generated value)
            const dbValue =
              value !== undefined && value !== null && value !== '' ? String(value) : '';
            const currentValue = dbValue && dbValue !== compositeValue ? dbValue : compositeValue;

            return (
              <div>
                <Input
                  type="number"
                  placeholder={interpolatedPlaceholder || 'Enter value...'}
                  value={currentValue}
                  onChange={e => handleChange(e.target.value, 'number')}
                  onBlur={question.validationType ? handleBlur : undefined}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                  onWheel={e => e.currentTarget.blur()}
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          } else {
            // For non-allowAppend composite fields, keep them read-only
            return (
              <div>
                <Input
                  type="number"
                  placeholder={interpolatedPlaceholder}
                  value={compositeValue}
                  onChange={e => handleChange(e.target.value, 'number')}
                  onBlur={question.validationType ? handleBlur : undefined}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                  onWheel={e => e.currentTarget.blur()}
                  readOnly
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          }
        }

        return (
          <div>
            <Input
              type="number"
              placeholder={interpolatedPlaceholder}
              value={question.validationType ? displayValue : (value as number) || ''}
              onChange={e => handleChange(e.target.value, 'number')}
              onBlur={question.validationType ? handleBlur : undefined}
              className={getErrorStyling('border-primary-200')}
              onWheel={e => e.currentTarget.blur()}
              data-private
            />
            {renderValidationError()}
          </div>
        );
      }

      case 'textselect':
      case 'select': {
        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const allowAppend = question.composite.allowAppendText ?? false;

          // Derive the composite option id from the source field (with optional mapping)
          // Select components expect option IDs, not formatted display values
          const getCompositeSelectValue = () => {
            const refs = question.composite?.questionRefs || [];
            if (!refs.length) return (value as string) || '';

            const sourceRef = refs[0];
            const raw = state.formData[sourceRef];

            if (raw === undefined || raw === null || raw === '') {
              return (value as string) || '';
            }

            // Apply mapping if defined
            if (question.composite?.mapping && question.composite.mapping[String(raw)]) {
              return question.composite.mapping[String(raw)];
            }

            return String(raw);
          };

          const compositeOptionId = getCompositeSelectValue();

          if (allowAppend) {
            // Allow user to override composite value
            // If DB value exists and differs from composite value, use DB value (user has edited)
            // Otherwise, use composite value (initialize with auto-generated value)
            const dbValue = (value as string) || '';
            const currentValue =
              dbValue && dbValue !== compositeOptionId ? dbValue : compositeOptionId;

            return (
              <div>
                <Select
                  value={currentValue}
                  onValueChange={newValue => handleSelectChange(newValue, onChange)}
                >
                  <SelectTrigger className={getErrorStyling('border-primary-200')}>
                    <SelectValue
                      placeholder="Select an option"
                      data-private
                    />
                  </SelectTrigger>
                  <SelectContent className="max-w-[90vw]">
                    {renderClearOption(!!currentValue)}
                    {interpolatedOptions?.map((option, index) => {
                      const isOwnershipDisabled =
                        option.wizardOnly &&
                        !isWizard &&
                        !interpolatedOptions.slice(0, index).some(o => o.wizardOnly);
                      return (
                        <React.Fragment key={option.id}>
                          {isOwnershipDisabled && hasWizardOnlyOptions && (
                            <>
                              <SelectSeparator />
                              <div className="max-w-xs px-2 py-2 text-xs text-gray-500">
                                {question.helperText}
                              </div>
                              <SelectSeparator />
                            </>
                          )}
                          <SelectItem
                            value={option.id}
                            className="whitespace-normal break-words py-3 sm:py-1.5"
                            disabled={option.wizardOnly && !isWizard}
                            data-private
                          >
                            {option.value}
                          </SelectItem>
                        </React.Fragment>
                      );
                    })}
                  </SelectContent>
                </Select>
                {renderValidationError()}
              </div>
            );
          } else {
            // For non-allowAppend composite fields, keep them read-only/disabled
            return (
              <div>
                <Select
                  value={compositeOptionId}
                  onValueChange={newValue => handleSelectChange(newValue, onChange)}
                  disabled={question.disabled}
                >
                  <SelectTrigger className={getErrorStyling('border-primary-200')}>
                    <SelectValue
                      placeholder="Select an option"
                      data-private
                    />
                  </SelectTrigger>
                  <SelectContent className="max-w-[90vw]">
                    {renderClearOption(!!compositeOptionId)}
                    {interpolatedOptions?.map((option, index) => {
                      const isFirstDisabled =
                        option.wizardOnly &&
                        !isWizard &&
                        !interpolatedOptions.slice(0, index).some(o => o.wizardOnly);
                      return (
                        <React.Fragment key={option.id}>
                          {isFirstDisabled && hasWizardOnlyOptions && (
                            <>
                              <SelectSeparator />
                              <div className="max-w-xs px-2 py-2 text-xs text-gray-500">
                                {question.helperText}
                              </div>
                              <SelectSeparator />
                            </>
                          )}
                          <SelectItem
                            value={option.id}
                            className="whitespace-normal break-words py-3 sm:py-1.5"
                            disabled={option.wizardOnly && !isWizard}
                            data-private
                          >
                            {option.value}
                          </SelectItem>
                        </React.Fragment>
                      );
                    })}
                  </SelectContent>
                </Select>
                {renderValidationError()}
              </div>
            );
          }
        }

        return (
          <div>
            <Select
              value={(value as string) || ''}
              onValueChange={newValue => handleSelectChange(newValue, onChange)}
            >
              <SelectTrigger className={getErrorStyling('border-primary-200')}>
                <SelectValue
                  placeholder="Select an option"
                  data-private
                />
              </SelectTrigger>
              <SelectContent className="max-w-[90vw]">
                {renderClearOption(!!value)}
                {interpolatedOptions?.map((option, index) => {
                  const isFirstDisabled =
                    option.wizardOnly &&
                    !isWizard &&
                    !interpolatedOptions.slice(0, index).some(o => o.wizardOnly);
                  return (
                    <React.Fragment key={option.id}>
                      {isFirstDisabled && hasWizardOnlyOptions && (
                        <>
                          <SelectSeparator />
                          <div className="px-2 py-2 text-xs text-gray-500">
                            {question.helperText}
                          </div>
                          <SelectSeparator />
                        </>
                      )}
                      <SelectItem
                        value={option.id}
                        className="whitespace-normal break-words py-3 sm:py-1.5"
                        disabled={option.wizardOnly && !isWizard}
                        data-private
                      >
                        {option.value}
                      </SelectItem>
                    </React.Fragment>
                  );
                })}
              </SelectContent>
            </Select>
            {renderValidationError()}
          </div>
        );
      }

      //case 'textarea-radio':
      case 'radio': {
        const getCompositeRadioValue = () => {
          if (!question.composite || !question.composite.questionRefs) {
            return (value as string) || '';
          }

          // Check if composite should be applied based on conditional logic
          if (!shouldApplyComposite(question, state.formData, parameters)) {
            return (value as string) || '';
          }

          // For radio fields, get the raw option ID from the source field directly
          // instead of using calculateCompositeValue which formats it as display text
          const sourceQuestionRef = question.composite.questionRefs[0];
          const rawValue = state.formData[sourceQuestionRef];

          if (!rawValue) {
            return (value as string) || '';
          }

          // If there's a mapping defined in the composite config, use it
          if (question.composite.mapping && question.composite.mapping[String(rawValue)]) {
            return question.composite.mapping[String(rawValue)];
          }

          return String(rawValue);
        };

        let effectiveValue: string = (value as string) || '';
        let isCompositeDisabled: boolean = !!question.disabled;

        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const allowAppend = question.composite.allowAppendText ?? false;
          const compositeValue = getCompositeRadioValue();

          if (allowAppend) {
            // Allow user to override composite value
            // If DB value exists and differs from composite value, use DB value (user has edited)
            // Otherwise, use composite value (initialize with auto-generated value)
            const dbValue = (value as string) || '';
            effectiveValue = dbValue && dbValue !== compositeValue ? dbValue : compositeValue;
            isCompositeDisabled = !!question.disabled; // Respect question.disabled even when allowAppend is true
          } else {
            // For non-allowAppend composite fields, use composite value and disable
            effectiveValue = compositeValue;
            isCompositeDisabled = true; // Composite forces disabled when allowAppend is false
          }
        }

        return (
          <div data-private>
            <RadioGroup
              disabled={isCompositeDisabled}
              hidden={question.hidden}
              value={effectiveValue}
              onValueChange={(value: string) => onChange(value)}
              className={cn('flex gap-4', {
                'flex-col': question.format === 'vertical',
                'flex-col sm:flex-row': question.format === 'horizontal',
                'opacity-50': isCompositeDisabled,
              })}
            >
              {interpolatedOptions?.map(option => {
                const radioId = `${question.id}-${option.id}`;
                const isSelected = effectiveValue === option.id;

                return (
                  <Label
                    key={option.id}
                    htmlFor={radioId}
                    className={cn('flex cursor-pointer items-center space-x-2 p-1', {
                      'cursor-not-allowed': isCompositeDisabled,
                    })}
                    onClick={(event: React.MouseEvent<HTMLLabelElement>) => {
                      if (isSelected && !question.required && !isCompositeDisabled) {
                        event.preventDefault();
                        onChange('');
                      }
                    }}
                  >
                    <RadioGroupItem
                      value={option.id}
                      id={radioId}
                      className="size-[18px]"
                    />
                    <span>{option.value}</span>
                  </Label>
                );
              })}
            </RadioGroup>
            {renderValidationError()}
          </div>
        );
      }

      case 'multiselect':
        return (
          <div>
            <MultiSelectQuestion
              question={question}
              value={value as string}
              onChange={onChange as (value: string) => void}
              disabled={question.disabled}
            />
            {renderValidationError()}
          </div>
        );

      case 'textarea':
        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const allowAppend = question.composite.allowAppendText ?? false;
          const compositeFieldRef = question.composite.compositeFieldRef;
          const compositeValue = calculateCompositeValue(
            question,
            state.formData,
            formDefinition.questions,
            parameters,
          );

          if (allowAppend && compositeFieldRef) {
            // Render two separate text boxes with separate storage:
            // 1. Read-only composite value (stored in compositeFieldRef, always auto-calculated)
            // 2. Editable user text field (stored in this question's field, user's custom text)
            //
            // Storage model:
            // - specialInstructions (this field) = user's custom text
            // - specialInstructionsComposite (compositeFieldRef) = auto-calculated composite value
            // Note: The composite value is automatically calculated and saved by calculateCompositeFieldValues
            // in NavigationContext during auto-save, so we don't need to update it here
            const userCustomText = (value as string) || '';

            return (
              <div className="space-y-2">
                {/* Read-only composite value */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Prefilled Information</Label>
                  {question.composite.compositeTitle && (
                    <p className="mb-2 text-sm text-gray-600">
                      {question.composite.compositeTitle}
                    </p>
                  )}
                  <Textarea
                    value={compositeValue}
                    rows={4}
                    className="border-primary-200 bg-gray-50"
                    disabled
                    readOnly
                    data-private
                  />
                </div>
                {/* Editable user text field */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Additional Information
                  </Label>
                  <Textarea
                    placeholder={interpolatedPlaceholder || 'Enter additional information...'}
                    value={userCustomText}
                    onChange={e => handleChange(e.target.value)}
                    rows={4}
                    className={getErrorStyling('border-primary-200')}
                    disabled={question.disabled}
                    data-private
                  />
                  {renderValidationError()}
                </div>
              </div>
            );
          }

          if (allowAppend) {
            // Single editable field with composite value
            // If DB value exists and differs from composite value, use DB value (user has edited)
            // Otherwise, use composite value (initialize with auto-generated text)
            const dbValue = (value as string) || '';
            const currentValue = dbValue && dbValue !== compositeValue ? dbValue : compositeValue;

            return (
              <div>
                <Textarea
                  placeholder={interpolatedPlaceholder || 'Enter additional information...'}
                  value={currentValue}
                  onChange={e => handleChange(e.target.value)}
                  onBlur={question.validationType ? handleBlur : undefined}
                  rows={6}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          } else {
            // For non-allowAppend composite fields, keep them read-only
            return (
              <div>
                <Textarea
                  placeholder={interpolatedPlaceholder}
                  value={compositeValue}
                  onChange={e => handleChange(e.target.value)}
                  rows={4}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                  readOnly
                  data-private
                />
                {renderValidationError()}
              </div>
            );
          }
        }

        return (
          <div>
            <Textarea
              placeholder={question.placeholder}
              value={(value as string) || ''}
              onChange={e => onChange(e.target.value)}
              rows={4}
              className={getErrorStyling('border-primary-200')}
              maxLength={question.characterLimit?.mode === 'strict' ? question.characterLimit.limit : undefined}
              data-private
            />
            {question.characterLimit && (
              <div className="mt-1 text-xs text-gray-500">
                {((value as string) || '').length} / {question.characterLimit.limit} characters
                {question.characterLimit.mode === 'overflow' && ((value as string) || '').length > question.characterLimit.limit && (
                  <span className="ml-2 text-orange-600">
                    (will be moved to overflow form)
                  </span>
                )}
              </div>
            )}
            {renderValidationError()}
          </div>
        );

      case 'date': {
        if (question.composite && shouldApplyComposite(question, state.formData, parameters)) {
          const compositeValue = calculateCompositeValue(
            question,
            state.formData,
            formDefinition.questions,
            parameters,
          );

          // Convert to ISO date format for HTML date input
          let isoCompositeDate = '';
          if (compositeValue) {
            const parsedDate = new Date(compositeValue);
            if (!isNaN(parsedDate.getTime())) {
              isoCompositeDate = parsedDate.toISOString().split('T')[0];
            }
          }

          // Support allowAppendText for dates (allow user override)
          if (question.composite.allowAppendText) {
            const dbValue = value ? new Date(value as string).toISOString().split('T')[0] : '';
            const displayValue =
              dbValue && dbValue !== isoCompositeDate ? dbValue : isoCompositeDate;

            return (
              <div data-private>
                <Input
                  type="date"
                  value={displayValue}
                  onChange={e => onChange(e.target.value)}
                  className={getErrorStyling('border-primary-200')}
                  disabled={question.disabled}
                />
                {renderValidationError()}
              </div>
            );
          }

          // Read-only composite date (no allowAppendText)
          return (
            <div data-private>
              <Input
                type="date"
                value={isoCompositeDate}
                onChange={e => onChange(e.target.value)}
                className={getErrorStyling('border-primary-200')}
                disabled
                readOnly
              />
              {renderValidationError()}
            </div>
          );
        }

        return (
          <div data-private>
            <Input
              type="date"
              value={value ? new Date(value as string).toISOString().split('T')[0] : ''}
              onChange={e => onChange(e.target.value)}
              className={getErrorStyling('border-primary-200')}
            />
            {renderValidationError()}
          </div>
        );
      }

      case 'table':
        return (
          <div>
            <TableQuestion
              question={question}
              value={value as Array<Record<string, any>> | undefined}
              onChange={onChange as (value: Array<Record<string, any>>) => void}
              isWizard={isWizard}
            />
            {renderValidationError()}
          </div>
        );

      default:
        return (
          <div>
            <Input
              type="text"
              placeholder={question.placeholder}
              value={(value as string) || ''}
              onChange={e => onChange(e.target.value)}
              className={getErrorStyling('border-primary-200')}
              data-private
            />
            {renderValidationError()}
          </div>
        );
    }
  };

  return (
    <div
      className={cn('space-y-2', {
        'invisible h-0 p-0': question.hidden,
      })}
    >
      <Label
        htmlFor={question.id}
        className="text-sm font-medium text-gray-700"
      >
        <div className="flex items-start justify-start">
          <div>
            <ReactMarkdown
              components={{
                p: ({ node, ...props }) => (
                  <p
                    {...props}
                    className="whitespace-pre-wrap"
                  >
                    {props.children}
                    {question.required && <span className="ml-1 text-red-500">*</span>}
                  </p>
                ),
                strong: ({ node, ...props }) => (
                  <strong
                    className="font-semibold"
                    {...props}
                  />
                ),
                em: ({ node, ...props }) => (
                  <em
                    className="italic"
                    {...props}
                  />
                ),
                u: ({ node, ...props }) => <u {...props} />,
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc pl-4 pt-1"
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    className="list-decimal pl-4 pt-1"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li
                    className="my-1"
                    {...props}
                  />
                ),
              }}
            >
              {interpolatedTitle}
            </ReactMarkdown>
          </div>
          {questionCitations && (
            <QuestionCitationTooltip
              citations={questionCitations}
              className="ml-2"
            />
          )}
        </div>
      </Label>
      {renderField()}
      {interpolatedHelperText && (
        <div className="text-sm">
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => (
                <p
                  {...props}
                  className="whitespace-pre-wrap"
                />
              ),
              strong: ({ node, ...props }) => (
                <strong
                  className="font-semibold"
                  {...props}
                />
              ),
              em: ({ node, ...props }) => (
                <em
                  className="italic"
                  {...props}
                />
              ),
              u: ({ node, ...props }) => <u {...props} />,
              br: ({ node, ...props }) => <br {...props} />,
            }}
          >
            {interpolatedHelperText}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default QuestionField;

interface MultiSelectQuestionProps {
  question: PolicyFormQuestion;
  value: PolicyFormSubmissionDataValue | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const MultiSelectQuestion: React.FC<MultiSelectQuestionProps> = ({
  question,
  value,
  onChange,
  disabled,
}) => {
  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    if (typeof value === 'string') return value.split(',').filter(Boolean);
    if (Array.isArray(value)) return value.map(String);
    return [String(value)];
  }, [value]);

  const handleMultiSelectChange = (optionId: string, checked: boolean) => {
    let newSelectedValues: string[];

    if (checked) {
      newSelectedValues = [...selectedValues, optionId];
    } else {
      newSelectedValues = selectedValues.filter(id => id !== optionId);
    }

    onChange(newSelectedValues.join(','));
  };

  return (
    <TooltipProvider>
      <div
        className={cn('flex flex-wrap gap-3', {
          'flex-col': question.format === 'vertical',
          'flex-col sm:flex-row': question.format === 'horizontal',
          'opacity-50': disabled,
        })}
        data-private
      >
        {question.options?.map(option => {
          const optionContent = (
            <div
              key={option.id}
              className={cn(
                'hover:bg-primary-100 flex w-max items-center space-x-2 rounded-md p-2',
                {
                  'w-full': question.format === 'vertical',
                  'cursor-not-allowed': disabled,
                },
              )}
            >
              <Checkbox
                id={`${question.id}-${option.id}`}
                checked={selectedValues.includes(option.id)}
                onCheckedChange={checked => handleMultiSelectChange(option.id, checked === true)}
                className="border-gray-300 bg-gray-100"
                disabled={disabled}
              />
              <Label
                htmlFor={`${question.id}-${option.id}`}
                className={cn('cursor-pointer', {
                  'cursor-not-allowed': disabled,
                })}
              >
                {option.value}
              </Label>
            </div>
          );

          if (option.tooltip) {
            return (
              <Tooltip key={option.id}>
                <TooltipTrigger asChild>{optionContent}</TooltipTrigger>
                <TooltipContent>
                  <p>{option.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            );
          }

          return optionContent;
        })}
      </div>
    </TooltipProvider>
  );
};

interface TableQuestionProps {
  question: PolicyFormQuestion;
  value: Array<Record<string, any>> | undefined;
  onChange: (value: Array<Record<string, any>>) => void;
  isWizard: boolean;
}

const TableQuestion: React.FC<TableQuestionProps> = ({
  question,
  value = [],
  onChange,
  isWizard,
}) => {
  const minRows = question.minRows || 1;
  const maxRows = question.maxRows || 10;
  const { validationState } = useNavigation();

  // value may not always be an array since form question types can change
  const safeValue = Array.isArray(value) ? value : [];

  const rows =
    safeValue.length < minRows
      ? [...safeValue, ...Array(minRows - safeValue.length).fill({})]
      : safeValue;

  const addRow = () => {
    if (rows.length < maxRows) {
      const newRow: Record<string, any> = {};
      question.columns?.forEach(col => {
        newRow[col.id] = '';
      });
      onChange([...rows, newRow]);
    }
  };

  const removeRow = (index: number) => {
    if (rows.length > minRows) {
      const newRows = rows.filter((_, i) => i !== index);
      onChange(newRows);
    }
  };

  const updateRow = (index: number, columnId: string, newValue: any) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [columnId]: newValue };
    onChange(newRows);
  };

  // Helper function to determine if a cell should show error styling
  const getCellErrorStyling = (column: PolicyFormTableColumn, cellValue: any) => {
    const tableHasValidationError = validationState.fieldValidationErrors.has(question.questionRef);
    const shouldShowError = isTableCellMissingRequiredValue(
      column,
      cellValue,
      tableHasValidationError,
    );

    return shouldShowError ? 'border-red-500' : 'border-primary-200';
  };

  const renderCellInput = (column: PolicyFormTableColumn, rowIndex: number, cellValue: any) => {
    const inputId = `${question.id}-${rowIndex}-${column.id}`;

    switch (column.type) {
      case 'text':
        return (
          <Input
            id={inputId}
            type="text"
            placeholder={column.placeholder}
            value={cellValue || ''}
            onChange={e => updateRow(rowIndex, column.id, e.target.value)}
            className={`w-full ${getCellErrorStyling(column, cellValue)}`}
            data-private
          />
        );

      case 'number':
        return (
          <Input
            id={inputId}
            type="number"
            placeholder={column.placeholder}
            value={cellValue || ''}
            onChange={e => updateRow(rowIndex, column.id, Number(e.target.value) || '')}
            className={`w-full ${getCellErrorStyling(column, cellValue)}`}
            onWheel={e => e.currentTarget.blur()}
            data-private
          />
        );

      case 'date':
        return (
          <Input
            id={inputId}
            type="date"
            value={cellValue || ''}
            onChange={e => updateRow(rowIndex, column.id, e.target.value)}
            className={`w-full ${getCellErrorStyling(column, cellValue)}`}
            data-private
          />
        );

      case 'textselect':
      case 'select':
        return (
          <Select
            value={cellValue || ''}
            onValueChange={newValue =>
              handleSelectChange(newValue, value => updateRow(rowIndex, column.id, value))
            }
          >
            <SelectTrigger className={`w-full ${getCellErrorStyling(column, cellValue)}`}>
              <SelectValue
                placeholder="Select..."
                data-private
              />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)] sm:max-w-80">
              {renderClearOption(!!cellValue)}
              {column.options?.map((option, index) => {
                const hasWizardOnlyOptions = column.options?.some(o => o.wizardOnly) ?? false;
                const isFirstDisabled =
                  option.wizardOnly &&
                  !isWizard &&
                  !column.options?.slice(0, index).some(o => o.wizardOnly);
                return (
                  <React.Fragment key={option.id}>
                    {isFirstDisabled && hasWizardOnlyOptions && question.helperText && (
                      <>
                        <SelectSeparator />
                        <div className="max-w-xs px-2 py-2 text-xs text-gray-500">
                          {question.helperText}
                        </div>
                        <SelectSeparator />
                      </>
                    )}
                    <SelectItem
                      value={option.id}
                      className="whitespace-normal break-words py-3 sm:py-1.5"
                      disabled={option.wizardOnly && !isWizard}
                      data-private
                    >
                      {option.value}
                    </SelectItem>
                  </React.Fragment>
                );
              })}
            </SelectContent>
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup
            value={cellValue || ''}
            onValueChange={value => updateRow(rowIndex, column.id, value)}
            className="flex flex-wrap gap-3"
            data-private
          >
            {column.options?.map(option => (
              <Label
                key={option.id}
                htmlFor={`${question.id}-${rowIndex}-${column.id}-${option.id}`}
                className="flex cursor-pointer items-center space-x-2"
              >
                <RadioGroupItem
                  value={option.id}
                  id={`${question.id}-${rowIndex}-${column.id}-${option.id}`}
                  className="size-[18px]"
                />
                <span className="text-sm">{option.value}</span>
              </Label>
            ))}
          </RadioGroup>
        );

      default:
        return (
          <Input
            id={inputId}
            type="text"
            placeholder={column.placeholder}
            value={cellValue || ''}
            onChange={e => updateRow(rowIndex, column.id, e.target.value)}
            className={`w-full ${getCellErrorStyling(column, cellValue)}`}
            data-private
          />
        );
    }
  };

  return (
    <div className="mb-4 space-y-4">
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className="border-primary-200 bg-primary-50 relative rounded-xl border p-4 transition-shadow"
        >
          <div className="mb-4 flex items-center justify-between sm:mb-0">
            {rows.length > minRows && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeRow(rowIndex)}
                className="absolute right-1 top-1 h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                aria-label="Remove row"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div
            style={
              {
                '--cols-amount': question?.gridColumns || 2,
              } as any
            }
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[repeat(var(--cols-amount),1fr)]"
          >
            {question.columns?.map(column => (
              <div
                key={column.id}
                className="space-y-2"
              >
                <Label
                  htmlFor={`${question.id}-${rowIndex}-${column.id}`}
                  className="text-sm font-medium text-gray-700"
                >
                  {column.title}
                  {column.required && <span className="ml-1 text-red-500">*</span>}
                </Label>
                {renderCellInput(column, rowIndex, row[column.id])}
              </div>
            ))}
          </div>
        </div>
      ))}

      {rows.length < maxRows && (
        <Button
          type="button"
          variant="outline"
          onClick={addRow}
          className="w-full"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Another
        </Button>
      )}
    </div>
  );
};
